/**
 * Product Configurator Component
 *
 * Main configurator that combines all sub-components
 */

import { motion } from 'framer-motion';
import { useConfigurator } from './useConfigurator';
import { ColorSelector } from './ColorSelector';
import { AddOnSelector } from './AddOnSelector';
import { PriceCalculator } from './PriceCalculator';
import type { ProductVariant, AddOn } from './types';

interface ProductConfiguratorProps {
  productId: string;
  productName: string;
  productDescription: string;
  variants: ProductVariant[];
  addons: AddOn[];
  images?: string[];
  onAddToCart?: (configuration: any) => void;
}

export const ProductConfigurator = ({
  productId,
  productName,
  productDescription,
  variants,
  addons,
  images = [],
  onAddToCart,
}: ProductConfiguratorProps) => {
  const {
    configuration,
    totalPrice,
    isValid,
    errors,
    selectVariant,
    toggleAddOn,
  } = useConfigurator({
    id: productId,
    name: productName,
    description: productDescription,
    images,
  });

  const selectedAddOnIds = configuration.selectedAddOns.map((a) => a.id);

  const handleAddToCart = () => {
    if (onAddToCart && isValid) {
      onAddToCart({
        ...configuration,
        totalPrice,
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column - Product Image */}
        <div>
          <div className="sticky top-8">
            {/* Main Product Image */}
            <div className="aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
              {configuration.selectedVariant?.image || images[0] ? (
                <img
                  src={configuration.selectedVariant?.image || images[0]}
                  alt={productName}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <div className="text-6xl mb-4">🎮</div>
                  <div className="text-lg font-medium">PadBox GS</div>
                  <div className="text-sm">Select a color to preview</div>
                </div>
              )}
            </div>

            {/* Thumbnail Gallery (if multiple images) */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <button
                    key={index}
                    className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-brand-primary transition"
                  >
                    <img
                      src={image}
                      alt={`${productName} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Configuration */}
        <div className="space-y-8">
          {/* Product Info */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{productName}</h1>
            <p className="text-lg text-gray-600">{productDescription}</p>
          </div>

          {/* Color Selector */}
          <ColorSelector
            variants={variants}
            selectedVariant={configuration.selectedVariant}
            onSelect={selectVariant}
          />

          {/* D-Pad Add-ons */}
          <AddOnSelector
            addons={addons}
            selectedAddOnIds={selectedAddOnIds}
            onToggle={toggleAddOn}
            category="dpad"
            title="D-Pad Type"
            description="Choose your preferred D-Pad style"
          />

          {/* Adapter Add-ons */}
          <AddOnSelector
            addons={addons}
            selectedAddOnIds={selectedAddOnIds}
            onToggle={toggleAddOn}
            category="adapter"
            title="Compatibility Adapters"
            description="Add optional adapters for specific platforms"
          />

          {/* Price Calculator */}
          <PriceCalculator configuration={configuration} totalPrice={totalPrice} />

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="text-red-800 font-medium mb-2">Please fix the following:</div>
              <ul className="list-disc list-inside space-y-1 text-red-700">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Add to Cart Button */}
          <motion.button
            onClick={handleAddToCart}
            disabled={!isValid}
            whileHover={isValid ? { scale: 1.02 } : {}}
            whileTap={isValid ? { scale: 0.98 } : {}}
            className={`
              w-full py-4 px-8 rounded-lg font-bold text-lg transition-all
              ${isValid
                ? 'bg-brand-primary text-white hover:bg-brand-primary/90 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {isValid ? 'Add to Cart' : 'Please Select Options'}
          </motion.button>

          {/* Preorder Notice */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📦</div>
              <div>
                <div className="font-semibold text-orange-900 mb-1">Available for Preorder</div>
                <div className="text-sm text-orange-800">
                  Ships approximately 2 to 4 weeks after purchase
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
